import { useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, ChevronRight, ChevronLeft, Sparkles, Download, Check, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { useUser } from "@/contexts/UserContext";
import { api } from "@/lib/api";
import PawSpinner from "@/components/PawSpinner";
import GeneratingLoader from "@/components/GeneratingLoader";
import { usePawConfetti } from "@/components/PawConfetti";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const MAX_SAVED = 3;

function downloadImage(url, filename) {
  fetch(url)
    .then((r) => r.blob())
    .then((blob) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch(() => toast.error("Download failed"));
}

export default function CompleteItem() {
  const { id } = useParams();
  const { userId } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fireConfetti = usePawConfetti();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [journalLiked, setJournalLiked] = useState("");
  const [journalDisliked, setJournalDisliked] = useState("");
  const [wouldRepeat, setWouldRepeat] = useState(null);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [characterAction, setCharacterAction] = useState("");
  // Multi-generation state
  const [generatedPhotos, setGeneratedPhotos] = useState([]); // [{ url, saved }]
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [completing, setCompleting] = useState(false);

  const { data: item, isLoading } = useQuery({
    queryKey: ["bucket-item", id],
    queryFn: () => api.getBucketItem(id).then((r) => r.data),
  });

  const { data: characters = [] } = useQuery({
    queryKey: ["characters", userId],
    queryFn: () => api.listCharacters(userId).then((r) => r.data),
    enabled: !!userId,
  });

  const { data: quota } = useQuery({
    queryKey: ["quota", userId],
    queryFn: () => api.getGenerationQuota(userId).then((r) => r.data),
    enabled: !!userId && step === 3,
  });

  const savedCount = generatedPhotos.filter((p) => p.saved).length;
  const limitReached = savedCount >= MAX_SAVED;

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const { data } = await api.uploadFile(file, userId);
      setPhotoUrl(data.path);
      toast.success("Photo uploaded!");
    } catch {
      toast.error("Upload failed. Try again?");
      setPhotoPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleGenerate = async () => {
    if (!photoUrl || !selectedCharacter) return;
    setGenerating(true);
    try {
      const { data } = await api.generateMemoryPhoto({
        memory_photo_url: photoUrl,
        character_id: selectedCharacter.id,
        user_id: userId,
        character_action: characterAction.trim() || "present naturally in the scene",
      });
      setGeneratedPhotos((prev) => [
        { url: data.generated_photo_url, saved: false },
        ...prev,
      ]);
      queryClient.invalidateQueries({ queryKey: ["quota"] });
      toast.success("Memory photo generated!");
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Generation didn't work this time. Try again?"
      );
    } finally {
      setGenerating(false);
    }
  };

  const toggleSave = useCallback((idx) => {
    setGeneratedPhotos((prev) =>
      prev.map((p, i) => {
        if (i !== idx) return p;
        if (p.saved) return { ...p, saved: false };
        if (prev.filter((x) => x.saved).length >= MAX_SAVED) return p;
        return { ...p, saved: true };
      })
    );
  }, []);

  const handleComplete = async () => {
    setCompleting(true);
    const savedUrls = generatedPhotos.filter((p) => p.saved).map((p) => p.url);
    try {
      await api.createMemory({
        bucket_item_id: id,
        user_id: userId,
        photo_url: photoUrl,
        generated_photo_url: savedUrls[0] || null,
        generated_photo_urls: savedUrls,
        journal_liked: journalLiked,
        journal_disliked: journalDisliked,
        journal_would_repeat: wouldRepeat,
        character_id: selectedCharacter?.id || null,
      });
      queryClient.invalidateQueries();
      fireConfetti();
      toast.success("Adventure completed!");
      setTimeout(() => navigate("/"), 1500);
    } catch {
      toast.error("Something went wrong");
    } finally {
      setCompleting(false);
    }
  };

  const itemTitle = item?.title || "memory";
  const makeFilename = (idx) =>
    `${itemTitle.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}-${idx + 1}.jpg`;

  if (isLoading)
    return (
      <div className="flex justify-center py-20">
        <PawSpinner />
      </div>
    );
  if (!item)
    return (
      <div className="text-center py-20 text-text-secondary">
        Item not found
      </div>
    );

  return (
    <div
      className="space-y-6 py-2 max-w-lg mx-auto"
      data-testid="complete-item-screen"
    >
      <div>
        <button
          onClick={() => navigate(-1)}
          className="text-text-secondary text-sm mb-2 flex items-center gap-1 hover:text-text-primary transition-colors"
          data-testid="back-btn"
        >
          <ChevronLeft size={16} /> Back
        </button>
        <h1 className="font-heading text-2xl text-text-primary">
          {item.title}
        </h1>
        <div className="flex items-center gap-4 mt-3">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-colors ${
                s <= step ? "bg-blush" : "bg-border-pink"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Step 1: Photo */}
      {step === 1 && (
        <div className="space-y-4" data-testid="step-1-photo">
          <h2 className="font-heading text-lg text-text-primary">
            Add a photo from today
          </h2>
          {photoPreview ? (
            <div className="relative rounded-card overflow-hidden shadow-soft">
              <img
                src={photoPreview}
                alt="Upload"
                className="w-full aspect-[4/3] object-cover"
              />
              {uploading && (
                <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                  <PawSpinner />
                </div>
              )}
            </div>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-border-pink rounded-card aspect-[4/3] flex flex-col items-center justify-center gap-3 cursor-pointer hover:bg-cream/50 transition-colors"
              data-testid="photo-upload-zone"
            >
              <div className="w-16 h-16 rounded-full bg-blush/10 flex items-center justify-center">
                <Camera className="text-blush" size={28} />
              </div>
              <p className="text-sm text-text-secondary">
                Tap to upload a photo
              </p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoSelect}
            className="hidden"
            data-testid="photo-file-input"
          />
          <button
            onClick={() => setStep(2)}
            disabled={!photoUrl}
            className="w-full bg-blush hover:bg-blush-dark disabled:opacity-50 text-white py-3 rounded-pill text-sm font-medium transition-colors flex items-center justify-center gap-2"
            data-testid="step-1-next-btn"
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Step 2: Journal */}
      {step === 2 && (
        <div className="space-y-4" data-testid="step-2-journal">
          <h2 className="font-heading text-lg text-text-primary">
            Tell us about it
          </h2>
          <div>
            <label className="text-sm text-text-secondary mb-1.5 block">
              What did you love about it?
            </label>
            <textarea
              value={journalLiked}
              onChange={(e) => setJournalLiked(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 rounded-input border border-border-pink bg-white text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-blush/30 text-sm resize-none"
              placeholder="The best part was..."
              data-testid="journal-liked-input"
            />
          </div>
          <div>
            <label className="text-sm text-text-secondary mb-1.5 block">
              What didn't you love?
            </label>
            <textarea
              value={journalDisliked}
              onChange={(e) => setJournalDisliked(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 rounded-input border border-border-pink bg-white text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-blush/30 text-sm resize-none"
              placeholder="Not so great was..."
              data-testid="journal-disliked-input"
            />
          </div>
          <div>
            <label className="text-sm text-text-secondary mb-2 block">
              Would you do it again?
            </label>
            <div className="flex gap-3" data-testid="would-repeat-toggle">
              {["Yes", "Maybe", "No"].map((opt) => (
                <button
                  key={opt}
                  onClick={() => setWouldRepeat(opt.toLowerCase())}
                  className={`flex-1 py-2.5 rounded-pill text-sm font-medium transition-colors ${
                    wouldRepeat === opt.toLowerCase()
                      ? "bg-blush text-white"
                      : "bg-white text-text-secondary border border-border-pink"
                  }`}
                  data-testid={`repeat-${opt.toLowerCase()}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setStep(1)}
              className="flex-1 py-3 rounded-pill text-sm font-medium border border-border-pink text-text-secondary hover:bg-cream transition-colors"
            >
              Back
            </button>
            <button
              onClick={() => setStep(3)}
              className="flex-1 bg-blush hover:bg-blush-dark text-white py-3 rounded-pill text-sm font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="step-2-next-btn"
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Memory Photos */}
      {step === 3 && (
        <div className="space-y-4" data-testid="step-3-memory-photo">
          <h2 className="font-heading text-lg text-text-primary">
            Add a character to this memory
          </h2>
          <p className="text-sm text-text-secondary">
            Optional: select a character to generate memory photos
          </p>

          {/* Character selection */}
          {characters.length > 0 ? (
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {characters.map((char) => (
                <button
                  key={char.id}
                  onClick={() =>
                    setSelectedCharacter(
                      selectedCharacter?.id === char.id ? null : char
                    )
                  }
                  className={`flex flex-col items-center gap-1.5 shrink-0 p-2 rounded-card transition-all ${
                    selectedCharacter?.id === char.id
                      ? "bg-blush/10 ring-2 ring-blush"
                      : "hover:bg-cream"
                  }`}
                  data-testid={`select-character-${char.id}`}
                >
                  <Avatar className="w-16 h-16">
                    {(char.generated_preview_url ||
                      char.reference_photos?.[0]) && (
                      <AvatarImage
                        src={api.getFileUrl(
                          char.generated_preview_url ||
                            char.reference_photos?.[0]
                        )}
                      />
                    )}
                    <AvatarFallback className="bg-cream text-blush font-heading">
                      {char.name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs text-text-secondary">
                    {char.name}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-text-secondary">
              No characters yet.{" "}
              <Link to="/characters/new" className="text-blush-dark underline">
                Create one
              </Link>
            </p>
          )}

          {/* Action + Generate button */}
          {selectedCharacter && !generating && (
            <div className="space-y-3">
              <div>
                <label className="text-sm text-text-secondary mb-1.5 block">
                  What is {selectedCharacter.name} doing in this memory?
                </label>
                <input
                  type="text"
                  value={characterAction}
                  onChange={(e) => setCharacterAction(e.target.value)}
                  placeholder="e.g. sitting beside me, watching from the corner"
                  className="w-full px-4 py-3 rounded-input border border-border-pink bg-white text-text-primary placeholder:text-text-secondary/40 focus:outline-none focus:ring-2 focus:ring-blush/30 text-sm"
                  data-testid="character-action-input"
                />
              </div>
              {quota && (
                <p
                  className="text-xs text-text-secondary"
                  data-testid="generation-quota"
                >
                  {quota.remaining} of 20 generations remaining today
                </p>
              )}
              <button
                onClick={handleGenerate}
                disabled={quota && quota.remaining <= 0}
                className="w-full bg-gold hover:bg-gold/80 disabled:opacity-50 text-white py-3 rounded-pill text-sm font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="generate-memory-photo-btn"
              >
                {generatedPhotos.length > 0 ? (
                  <>
                    <RotateCw size={16} /> Generate again
                  </>
                ) : (
                  <>
                    <Sparkles size={16} /> Create a memory with{" "}
                    {selectedCharacter.name}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Generating loader */}
          {generating && (
            <GeneratingLoader
              message={`Adding ${selectedCharacter?.name} to your memory...`}
            />
          )}

          {/* Generated photos gallery */}
          {generatedPhotos.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs text-text-secondary font-medium">
                Generated photos ({savedCount}/{MAX_SAVED} saved)
              </p>
              <div className="space-y-3">
                {generatedPhotos.map((photo, idx) => (
                  <div
                    key={photo.url}
                    className={`rounded-card overflow-hidden shadow-soft border-2 transition-colors ${
                      photo.saved
                        ? "border-sage"
                        : "border-transparent"
                    }`}
                    data-testid={`generated-photo-${idx}`}
                  >
                    <div className="relative">
                      <img
                        src={api.getFileUrl(photo.url)}
                        alt={`Generated ${idx + 1}`}
                        className="w-full"
                      />
                      {/* Download button */}
                      <button
                        onClick={() =>
                          downloadImage(
                            api.getFileUrl(photo.url),
                            makeFilename(idx)
                          )
                        }
                        className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition-colors"
                        data-testid={`download-gen-${idx}`}
                      >
                        <Download size={16} className="text-white" />
                      </button>
                      {photo.saved && (
                        <div className="absolute top-2 left-2 bg-sage text-white text-xs px-2.5 py-0.5 rounded-pill font-medium flex items-center gap-1">
                          <Check size={12} /> Saved
                        </div>
                      )}
                    </div>
                    {/* Save/unsave button */}
                    <button
                      onClick={() => toggleSave(idx)}
                      disabled={!photo.saved && limitReached}
                      className={`w-full py-2.5 text-sm font-medium transition-colors ${
                        photo.saved
                          ? "text-sage hover:bg-sage/5"
                          : limitReached
                          ? "text-text-secondary/50 cursor-not-allowed"
                          : "text-blush-dark hover:bg-cream"
                      }`}
                      data-testid={`save-gen-${idx}`}
                    >
                      {photo.saved
                        ? "Unsave"
                        : limitReached
                        ? `${MAX_SAVED} saved — limit reached`
                        : "Save to memory"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Complete button */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setStep(2)}
              className="flex-1 py-3 rounded-pill text-sm font-medium border border-border-pink text-text-secondary hover:bg-cream transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleComplete}
              disabled={completing}
              className="flex-1 bg-blush hover:bg-blush-dark disabled:opacity-50 text-white py-3 rounded-pill text-sm font-medium transition-colors flex items-center justify-center"
              data-testid="complete-adventure-btn"
            >
              {completing ? (
                <PawSpinner size="sm" />
              ) : (
                "Complete adventure"
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
